import React, { Fragment, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown, CheckCircle } from 'lucide-react';
import { Card } from '../ui/Card';
interface UseCase {
  title: string;
  link: string;
}
interface ProductSectionProps {
  id: string;
  title: string;
  overview: string;
  processSteps: string[];
  useCases: UseCase[];
  icon: React.ElementType;
  reverse?: boolean;
}
// Diagram Components - Normalized sizing
const OrchestrateDiagram = () => <div className="w-full" data-id="element-385">
    <motion.div initial={{
    opacity: 0,
    y: -10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-blue-600 text-white rounded-lg p-4 text-center font-semibold mb-6 sm:mb-8" data-id="element-386">
      Orchestrate
    </motion.div>

    <div className="hidden sm:flex justify-center gap-4 mb-6" data-id="element-387">
      {[1, 2, 3, 4, 5].map(i => <div key={i} className="w-px h-12 bg-slate-300" data-id="element-388" />)}
    </div>
    <div className="flex sm:hidden justify-center mb-4" data-id="element-389">
      <ArrowDown className="w-5 h-5 text-slate-400" data-id="element-390" />
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.1
  }} className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-391">
      {['Payments', 'Wallets', 'Exchange', 'Liquidity', 'Compliance'].map((product, i) => <div key={i} className="bg-white border-2 border-slate-300 rounded p-3 text-center" data-id="element-392">
            <span className="text-xs font-medium text-slate-700" data-id="element-393">
              {product}
            </span>
          </div>)}
    </motion.div>
  </div>;
const PaymentsDiagram = () => <div className="w-full" data-id="element-394">
    {/* Desktop & Tablet: Horizontal flow */}
    <div className="hidden sm:block" data-id="element-395">
      <div className="flex items-center justify-between gap-2 md:gap-3" data-id="element-396">
        {['Sender', 'Local Rail', 'FX', 'Global Rail', 'Recipient'].map((step, i) => <Fragment key={i} data-id="element-397">
              <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className={`${i === 2 ? 'bg-slate-100 border-dashed' : 'bg-white border-solid'} border-2 border-slate-300 rounded-lg p-3 md:p-4 text-center flex-1 min-w-0`} data-id="element-398">
                <span className="text-xs md:text-sm font-semibold text-slate-700 block" data-id="element-399">
                  {step}
                </span>
                {i === 2 && <span className="text-[10px] text-slate-500 block mt-1" data-id="element-400">
                    Optional
                  </span>}
              </motion.div>
              {i < 4 && <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-slate-400 flex-shrink-0" data-id="element-401" />}
            </Fragment>)}
      </div>
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.4
    }} className="mt-6 md:mt-8 text-center" data-id="element-402">
        <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2" data-id="element-403">
          <CheckCircle className="w-4 h-4 text-green-600" data-id="element-404" />
          <span className="text-sm font-medium text-green-700" data-id="element-405">Settlement</span>
        </div>
      </motion.div>
    </div>

    {/* Mobile: Vertical flow */}
    <div className="block sm:hidden space-y-3" data-id="element-406">
      {['Sender', 'Local Rail', 'FX (Optional)', 'Global Rail', 'Recipient'].map((step, i) => <Fragment key={i} data-id="element-407">
          <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className={`${step.includes('Optional') ? 'bg-slate-100 border-dashed' : 'bg-white border-solid'} border-2 border-slate-300 rounded-lg p-4 text-center`} data-id="element-408">
            <span className="text-sm font-semibold text-slate-700" data-id="element-409">{step}</span>
          </motion.div>
          {i < 4 && <div className="flex justify-center" data-id="element-410">
              <ArrowDown className="w-5 h-5 text-slate-400" data-id="element-411" />
            </div>}
        </Fragment>)}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.4
    }} className="pt-2 text-center" data-id="element-412">
        <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2" data-id="element-413">
          <CheckCircle className="w-4 h-4 text-green-600" data-id="element-414" />
          <span className="text-sm font-medium text-green-700" data-id="element-415">Settlement</span>
        </div>
      </motion.div>
    </div>
  </div>;
const WalletsDiagram = () => <div className="w-full" data-id="element-416">
    <motion.div initial={{
    opacity: 0,
    y: -10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-slate-800 text-white rounded-lg p-4 text-center font-semibold mb-8" data-id="element-417">
      Master Wallet
    </motion.div>

    <motion.div initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.1
  }} className="grid grid-cols-3 gap-3 mb-8" data-id="element-418">
      {['Sub-Wallet A', 'Sub-Wallet B', 'Sub-Wallet C'].map((wallet, i) => <div key={i} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-419">
          <div className="text-sm font-semibold text-slate-700 mb-2" data-id="element-420">
            {wallet}
          </div>
          <div className="text-xs text-slate-500" data-id="element-421">Balance</div>
        </div>)}
    </motion.div>

    <div className="flex justify-center gap-2 mb-8" data-id="element-422">
      <ArrowRight className="w-5 h-5 text-slate-400" data-id="element-423" />
      <span className="text-sm text-slate-500" data-id="element-424">Internal Transfers</span>
      <ArrowRight className="w-5 h-5 text-slate-400" data-id="element-425" />
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.3
  }} className="bg-slate-100 border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-426">
      <span className="text-sm font-semibold text-slate-700" data-id="element-427">Ledger</span>
    </motion.div>
  </div>;
const ExchangeDiagram = () => <div className="w-full" data-id="element-428">
    {/* Desktop */}
    <div className="hidden sm:flex items-center justify-between gap-3 mb-12" data-id="element-429">
      {['Request', 'Quote', 'Execute', 'Settle'].map((step, i) => <Fragment key={i} data-id="element-430">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center flex-1" data-id="element-431">
            <span className="text-sm font-semibold text-slate-700" data-id="element-432">{step}</span>
          </motion.div>
          {i < 3 && <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-433" />}
        </Fragment>)}
    </div>
    {/* Mobile */}
    <div className="sm:hidden grid grid-cols-2 gap-3 mb-8" data-id="element-434">
      {['Request', 'Quote', 'Execute', 'Settle'].map((step, i) => <motion.div key={i} initial={{
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      delay: i * 0.05
    }} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-435">
            <span className="text-sm font-semibold text-slate-700" data-id="element-436">{step}</span>
          </motion.div>)}
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.3
  }} className="flex items-center justify-center gap-4" data-id="element-437">
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 sm:px-6 py-3 text-sm font-medium text-blue-700" data-id="element-438">
        Fiat
      </div>
      <div className="flex items-center gap-1 sm:gap-2" data-id="element-439">
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" data-id="element-440" />
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 rotate-180" data-id="element-441" />
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg px-4 sm:px-6 py-3 text-sm font-medium text-green-700" data-id="element-442">
        Stablecoin
      </div>
    </motion.div>
  </div>;
const ComplianceDiagram = () => <div className="w-full" data-id="element-443">
    <div className="flex items-center justify-between mb-6 sm:mb-10 gap-2" data-id="element-444">
      {['User', 'Transaction', 'Execution'].map((step, i) => <Fragment key={i} data-id="element-445">
          <motion.div initial={{
        opacity: 0,
        x: -10
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className="bg-white border-2 border-slate-300 rounded-lg p-3 sm:p-4 text-center flex-1" data-id="element-446">
            <span className="text-xs sm:text-sm font-semibold text-slate-700" data-id="element-447">{step}</span>
          </motion.div>
          {i < 2 && <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" data-id="element-448" />}
        </Fragment>)}
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2
  }} className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-id="element-449">
      {['Identity', 'Screening', 'Monitoring', 'Reporting'].map((checkpoint, i) => <div key={i} className="bg-red-50 border border-red-200 rounded p-3 text-center" data-id="element-450">
            <span className="text-xs font-medium text-red-700" data-id="element-451">
              {checkpoint}
            </span>
          </div>)}
    </motion.div>
  </div>;
const TokenizeDiagram = () => <div className="w-full" data-id="element-452">
    {/* Desktop */}
    <div className="hidden sm:flex items-center justify-between gap-3 mb-12" data-id="element-453">
      {['Mint', 'Hold', 'Transfer', 'Burn'].map((step, i) => <Fragment key={i} data-id="element-454">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center flex-1" data-id="element-455">
            <span className="text-sm font-semibold text-slate-700" data-id="element-456">{step}</span>
          </motion.div>
          {i < 3 && <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-457" />}
        </Fragment>)}
    </div>
    {/* Mobile */}
    <div className="sm:hidden grid grid-cols-2 gap-3 mb-8" data-id="element-458">
      {['Mint', 'Hold', 'Transfer', 'Burn'].map((step, i) => <motion.div key={i} initial={{
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      delay: i * 0.05
    }} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-459">
            <span className="text-sm font-semibold text-slate-700" data-id="element-460">{step}</span>
          </motion.div>)}
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.3
  }} className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center" data-id="element-461">
      <span className="text-sm font-semibold text-purple-700" data-id="element-462">
        Programmable Rules
      </span>
    </motion.div>
  </div>;
const LiquidityDiagram = () => <div className="w-full" data-id="element-463">
    <motion.div initial={{
    opacity: 0,
    y: -10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="grid grid-cols-3 gap-3 mb-8" data-id="element-464">
      {['Banks', 'Market Makers', 'On-Chain'].map((source, i) => <div key={i} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-465">
          <span className="text-sm font-semibold text-slate-700" data-id="element-466">{source}</span>
        </div>)}
    </motion.div>

    <div className="flex justify-center gap-4 mb-8" data-id="element-467">
      {[1, 2, 3].map(i => <ArrowDown key={i} className="w-5 h-5 text-slate-400" data-id="element-468" />)}
    </div>

    <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2
  }} className="bg-blue-600 text-white rounded-lg p-4 text-center font-semibold mb-8" data-id="element-469">
      Obseq Routing
    </motion.div>

    <div className="flex justify-center mb-8" data-id="element-470">
      <ArrowDown className="w-5 h-5 text-slate-400" data-id="element-471" />
    </div>

    <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.3
  }} className="bg-white border-2 border-slate-300 rounded-lg p-4 text-center" data-id="element-472">
      <span className="text-sm font-semibold text-slate-700" data-id="element-473">Execution</span>
    </motion.div>
  </div>;
const ConnectDiagram = () => <div className="w-full" data-id="element-474">
    {/* Desktop */}
    <div className="hidden sm:flex items-center justify-between gap-2 mb-8" data-id="element-475">
      {['Client App', 'Auth', 'Obseq APIs', 'Products', 'Webhooks'].map((step, i) => <Fragment key={i} data-id="element-476">
            <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className={`${i === 2 ? 'bg-blue-600 text-white' : 'bg-white border-2 border-slate-300 text-slate-700'} rounded-lg p-3 text-center flex-1`} data-id="element-477">
              <span className="text-xs font-semibold" data-id="element-478">{step}</span>
            </motion.div>
            {i < 4 && <ArrowRight className={`w-4 h-4 ${i === 3 ? 'rotate-180' : ''} text-slate-400 flex-shrink-0`} data-id="element-479" />}
          </Fragment>)}
    </div>
    {/* Mobile */}
    <div className="sm:hidden space-y-3 mb-6" data-id="element-480">
      {['Client App', 'Auth', 'Obseq APIs', 'Products', 'Webhooks'].map((step, i) => <Fragment key={i} data-id="element-481">
            <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className={`${i === 2 ? 'bg-blue-600 text-white' : 'bg-white border-2 border-slate-300 text-slate-700'} rounded-lg p-3 text-center`} data-id="element-482">
              <span className="text-sm font-semibold" data-id="element-483">{step}</span>
            </motion.div>
            {i < 4 && <div className="flex justify-center" data-id="element-484">
              <ArrowDown className={`w-4 h-4 ${i === 3 ? 'rotate-180' : ''} text-slate-400`} data-id="element-485" />
            </div>}
          </Fragment>)}
    </div>

    <motion.div initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.4
  }} className="flex justify-between text-xs text-slate-500" data-id="element-486">
      <span data-id="element-487">Request →</span>
      <span data-id="element-488">← Event</span>
    </motion.div>
  </div>;
export function ProductSection({
  id,
  title,
  overview,
  processSteps,
  useCases,
  icon: Icon,
  reverse = false
}: ProductSectionProps) {
  const renderDiagram = () => {
    switch (id) {
      case 'orchestrate':
        return <OrchestrateDiagram data-id="element-489" />;
      case 'payments':
        return <PaymentsDiagram data-id="element-490" />;
      case 'wallets':
        return <WalletsDiagram data-id="element-491" />;
      case 'exchange':
        return <ExchangeDiagram data-id="element-492" />;
      case 'compliance':
        return <ComplianceDiagram data-id="element-493" />;
      case 'tokenize':
        return <TokenizeDiagram data-id="element-494" />;
      case 'liquidity':
        return <LiquidityDiagram data-id="element-495" />;
      case 'connect':
        return <ConnectDiagram data-id="element-496" />;
      default:
        return <OrchestrateDiagram data-id="element-497" />;
    }
  };
  return <section id={id} className="py-20 border-b border-slate-100 scroll-mt-20" data-id="element-498">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-499">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-500">
          {/* Content Column */}
          <div className={`lg:col-span-6 flex flex-col justify-start ${reverse ? 'lg:col-start-7' : ''}`} data-id="element-501">
            {/* Title */}
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 mb-4 text-center sm:text-left" data-id="element-502">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-503">
                <Icon className="w-5 h-5" data-id="element-504" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" data-id="element-505">{title}</h2>
            </div>

            {/* Overview */}
            <p className="text-base sm:text-lg text-slate-600 mb-8 sm:mb-10 leading-relaxed text-center sm:text-left" data-id="element-506">
              {overview}
            </p>

            {/* Process Flow */}
            <div className="text-center sm:text-left" data-id="element-507">
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4" data-id="element-508">
                Process Flow
              </h3>
              <div className="relative pl-6 border-l-2 border-slate-100 space-y-6" data-id="element-509">
                {processSteps.map((step, index) => <div key={index} className="relative" data-id="element-510">
                    <div className="absolute -left-[29px] top-0 w-4 h-4 rounded-full bg-white border-2 border-blue-500 flex items-center justify-center" data-id="element-511">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" data-id="element-512" />
                    </div>
                    <p className="text-sm text-slate-700 font-medium" data-id="element-513">{step}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Diagram Column */}
          <div className={`lg:col-span-6 flex flex-col ${reverse ? 'lg:col-start-1 lg:row-start-1' : ''}`} data-id="element-514">
            {/* Diagram - 80% height of text area */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-slate-50 rounded-xl border border-slate-200 p-6 sm:p-8 mb-6 flex-1 flex items-center justify-center min-h-[400px]" data-id="element-515">
              <div className="w-full max-w-lg" data-id="element-516">{renderDiagram()}</div>
            </motion.div>

            {/* Use Cases - Below diagram */}
            <div className="text-center sm:text-left" data-id="element-517">
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3" data-id="element-518">
                Use Cases
              </h3>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2" data-id="element-519">
                {useCases.slice(0, 4).map((useCase, index) => <a key={index} href="#" className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100" data-id="element-520">
                    <span className="text-xs font-medium text-slate-700 group-hover:text-blue-700" data-id="element-521">
                      {useCase.title}
                    </span>
                    <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-0.5" data-id="element-522" />
                  </a>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}